import { useState, cloneElement } from 'react'
import { Link } from 'react-router-dom'
import { HiArrowUpRight, HiOutlineExclamationCircle } from 'react-icons/hi2'

import { FlagArgentina, FlagChile, FlagBrasil, FlagColombia, FlagMexico, FlagPeru, FlagUruguay } from '../../Icons/Flags'

import { Indicator } from './Indicator'

import { salary } from '../../i18n/translatedMessages'

export function ExpandingList({ view, employees, children }) {
	return (
		<div className='border tw-overflow-hidden tw-rounded-md'>
			<div className='tw-flex tw-w-full tw-gap-8 tw-px-6 tw-py-2 tw-bg-gray-200'>
				<div className='tw-flex tw-w-4/12 tw-font-bold'>{salary.employeeListEmployeeAndJob}</div>
				<div className='tw-flex tw-w-1/12 tw-font-bold'>{salary.employeeListPerformance}</div>
				<div className='tw-flex tw-w-1/12 tw-font-bold'>{salary.employeeListPotential}</div>
				<div className='tw-flex tw-w-2/12 tw-font-bold'>{salary.employeeListLevelAndArea}</div>
				<div className='tw-flex tw-w-4/12 tw-font-bold'>{salary.employeeListNextStep}</div>
				<div className='tw-flex tw-w-1/12 tw-font-bold'></div>
			</div>
			{[...employees]
				?.sort((a, b) => a.name.localeCompare(b.name))
				.map(employee => (
					<ItemExpanding key={employee.employee_id} employee={employee} view={view} children={children} />
				))}
		</div>
	)
}

const color = {
	employee: {
		primary: 'tw-bg-[#0D554A] tw-text-white',
		secondary: 'tw-bg-[#00A28B] tw-text-white',
		lightColor: 'tw-bg-[#C7FFF1] tw-text-[#00A28B]',
	},
	algorithm: {
		primary: 'tw-bg-[#084E72] tw-text-white',
		secondary: 'tw-bg-[#008ACD] tw-text-white',
		lightColor: 'tw-bg-[#DFF3FE] tw-text-[#008ACD]',
	},
	hr: { primary: 'tw-bg-[#40337B] tw-text-white', secondary: 'tw-bg-[#6C4FDB] tw-text-white', lightColor: 'tw-bg-[#E2E4FD] tw-text-[#6C4FDB]' },
}

const countries = {
	1: { flag: <FlagChile className='tw-w-full tw-h-full tw-flex' />, title: 'Chile' },
	2: { flag: <FlagArgentina className='tw-w-full tw-h-full tw-flex' />, title: 'Argentina' },
	3: { flag: <FlagPeru className='tw-w-full tw-h-full tw-flex' />, title: 'Peru' },
	4: { flag: <FlagBrasil className='tw-w-full tw-h-full tw-flex' />, title: 'Brasil' },
	5: { flag: <FlagColombia className='tw-w-full tw-h-full tw-flex' />, title: 'Colombia' },
	6: { flag: <FlagMexico className='tw-w-full tw-h-full tw-flex' />, title: 'Mexico' },
	7: { flag: '', title: 'Costa Rica' },
	8: { flag: <FlagUruguay className='tw-w-full tw-h-full tw-flex' />, title: 'Uruguay' },
}

const ItemExpanding = ({ employee, view, children }) => {
	const [isOpen, setIsOpen] = useState(false)

	const toggleAccordion = () => {
		setIsOpen(isOpen => !isOpen)
	}

	const cloneChild = cloneElement(children, { employee })

	return (
		<div className='tw-divide-y'>
			<div
				className={`tw-cursor-pointer border-top tw-transition-all ${
					isOpen ? color[view].primary : 'tw-bg-white tw-text-gray-800'
				} tw-flex tw-justify-between tw-items-center tw-w-full tw-p-4 tw-text-left tw-font-medium tw-focus:outline-none`}
				onClick={toggleAccordion}
			>
				<div className='tw-flex tw-w-full tw-gap-8 tw-p-2'>
					<div className='tw-flex tw-gap-5 tw-h-full tw-w-4/12'>
						<div className='tw-relative tw-w-20 tw-h-20'>
							<div
								className='tw-h-7 tw-w-7 tw-rounded-full tw-absolute tw-top-[-4px] tw-right-[-8px] tw-flex tw-items-center tw-justify-center'
								style={{ border: '2px solid #FFF' }}
							>
								{countries[employee?.country?.id]?.flag}
							</div>
							<div
								className='tw-rounded-full tw-overflow-hidden tw-w-20 tw-h-20 tw-bg-white tw-flex tw-items-center tw-justify-center'
								title={employee.employee_id}
							>
								<img src='https://assets.mydna.company/profile-placeholder.png' alt='' className='tw-w-20' />
							</div>
						</div>
						<div className='tw-flex tw-flex-col tw-justify-center tw-h-full'>
							<Link to={`/app/analytic/futurecv/${employee.employee_id}`} className={`${isOpen ? 'tw-text-white' : ''}`}>
								<h4 className='tw-text-base tw-font-bold'>
									{employee.name} <HiArrowUpRight size={16} />
								</h4>
							</Link>
							<p>{employee.jobname}</p>
						</div>
					</div>
					<div className='tw-flex tw-w-1/12'>{Number(employee.performance / 20).toFixed(1)}</div>
					<div className='tw-flex tw-w-1/12'>{Number(employee.potential / 20).toFixed(1)}</div>
					<div className='tw-flex tw-flex-wrap tw-w-2/12'>
						<span className='tw-text-base'>{employee.level.value}</span>
						<p className='tw-text-base'>{employee.area.value}</p>
					</div>
					<div className='tw-w-4/12 tw-space-y-2'>
						{employee[`next_steps_${view}`].length === 0 ? (
							<span className='tw-flex tw-gap-1 tw-items-center tw-text-base'>
								<HiOutlineExclamationCircle size={22} />
								{salary.notDefined}
							</span>
						) : (
							employee[`next_steps_${view}`]
								.filter((_, index) => index < 3)
								.map((item, index) => (
									<div key={index} className='tw-flex tw-items-center tw-gap-2'>
										<Indicator up={item.level.id > employee.level.id} className={color[view].lightColor} />
										<div key={index} className='tw-flex tw-flex-wrap tw-items-center tw-gap-x-2'>
											<span className={`tw-flex tw-gap-1 tw-text-base tw-items-center ${isOpen ? 'tw-text-gray-300' : 'tw-text-gray-700'}`}>
												{item.level.value}
											</span>
											{view === 'algorithm' ? (
												<span className={`tw-flex tw-gap-1 tw-text-base tw-items-center ${isOpen ? 'tw-text-white' : 'tw-text-gray-500'}`}>
													{item.area?.value}
												</span>
											) : (
												<span className={`tw-flex tw-gap-1 tw-text-base tw-items-center ${isOpen ? 'tw-text-white' : 'tw-text-gray-500'}`}>
													{item.subarea?.value}
												</span>
											)}
										</div>
									</div>
								))
						)}
					</div>
					<div className='tw-flex tw-justify-end tw-items-center tw-w-1/12'>
						<button
							className={`${
								isOpen ? color[view].secondary : 'tw-bg-white'
							} tw-w-[32px] tw-h-[32px] border-0 tw-rounded-full tw-flex tw-justify-center tw-items-center `}
						>
							<svg
								className={`tw-w-5 tw-h-5 tw-transition-all ${isOpen ? 'tw-rotate-180' : 'tw-rotate-0'}`}
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'
								xmlns='http://www.w3.org/2000/svg'
							>
								<path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7'></path>
							</svg>
						</button>
					</div>
				</div>
			</div>
			<div className={`tw-overflow-hidden tw-transition  ${isOpen ? 'tw-max-h-full' : 'tw-max-h-0'}`}>
				<div className='tw-p-4 tw-bg-white'>{cloneChild}</div>
			</div>
		</div>
	)
}
